import type { MouseEvent } from 'react';
import type { useMatches } from 'react-router';

export type HeaderConfig = {
  backLink?: string;
  /** Which icon the back link renders. `'arrow'` (default) is a step-back
   *  chevron; `'close'` is a cross, used when the link exits a flow rather
   *  than stepping back — e.g. leaving checkout for the experience page. */
  backIcon?: 'arrow' | 'close';
  /** Accessible name for the back/close link. Defaults to "Back". Callers
   *  using `backIcon: 'close'` should pass a matching label (e.g. checkout's
   *  "Close checkout") so the announced name fits the cross. */
  backLabel?: string;
  /** Optional click handler for the back button (e.g. analytics, or forcing a
   *  full document load). Fires alongside the `backLink` navigation; call
   *  `preventDefault()` to take over the navigation. */
  onBackClick?: (e: MouseEvent) => void;
  /**
   * Background surface for the header strip on mobile. Defaults to `base`.
   * Set to `raised` on routes that want the header to continue a raised
   * panel visually (e.g. the home page where the hero+search-button panel
   * directly below is also `surface.raised`). Tablet+ stays on `base`
   * regardless — only the mobile header bg is affected.
   */
  surface?: 'base' | 'raised';
};

/** Wider type for the route handle — extensible for future route-level config. */
export type RouteHandle = {
  header?: HeaderConfig;
};

/**
 * Read the HeaderConfig from the deepest matched route's handle.
 * Single controlled cast point — all producers use `satisfies RouteHandle` for compile-time safety.
 *
 * `urlLocale` is the optional `:locale?` URL segment (e.g. `'da'`). When
 * `undefined`, the user is on the default language and no locale prefix is
 * emitted. When present, the same raw segment the user arrived on is used so
 * back-links stay inside the user's current URL convention.
 */
export function getHeaderConfig(
  matches: ReturnType<typeof useMatches>,
  urlLocale: string | undefined,
  options?: { hasMultipleListedExperiences?: boolean }
): HeaderConfig {
  const leaf = matches.at(-1);
  const handle = leaf?.handle as RouteHandle | undefined;
  const config = handle?.header ?? {};

  if (!config.backLink) {
    return config;
  }

  // A route can gate its back link on where the visitor came from by returning
  // `showBackArrow: false` from its loader (see `routes/experience`). The arrow
  // links to the experience overview, which is only a sensible "back" target
  // when the visitor arrived from inside the storefront. Visitors who came from
  // an external CTA or the WP plugin get no arrow and use the browser's own
  // back control instead.
  const leafData = leaf?.loaderData as { showBackArrow?: boolean } | undefined;
  if (leafData?.showBackArrow === false) {
    return { ...config, backLink: undefined };
  }

  // Single-experience tenants are redirected from `/` straight to the
  // experience page (see `routes/home/route.tsx`). A back-link to `/`
  // would just bounce them back, so suppress it in that case.
  if (
    config.backLink === '/' &&
    options?.hasMultipleListedExperiences === false
  ) {
    return { ...config, backLink: undefined };
  }

  return {
    ...config,
    backLink: resolveLocalePath(config.backLink, urlLocale),
  };
}

/** Prepend the URL's locale prefix to a base path, if any. */
function resolveLocalePath(
  basePath: string,
  urlLocale: string | undefined
): string {
  if (!urlLocale) return basePath;
  return `/${urlLocale}${basePath === '/' ? '' : basePath}`;
}
